import React, { useEffect, useState } from 'react';
import { ReactComponent as JoyHeartIconSvg } from '@images/joy-heart.svg';
import { ReactComponent as RingsClosedIcon } from '@images/hamburger-icon.svg';
import { JoykitColor, LinkVariant } from '@components/themes';
import MobileAccordion from '@components/MobileAccordion/MobileAccordion';
import { CTALink } from '@components/CTALink';
import { ButtonVariant } from '@components/Button';
import { Columns } from '@components/Columns';
import { Box } from '@components/Box';
import { Flex } from '@components/Flex';
import { Text } from '@components/typography';
import { JoykitIcon } from '@components/JoykitIcon';
import {
  DesktopColumns,
  FindCoupleWrapper,
  StyledGetStartedButton,
  HeaderSpacingStub,
  MobileFindButton,
  MobileGetStartedButton,
  MobileLoginButton,
  MobileNavHeaderExternalLink,
  MobileNavHeaderLink,
  MobileNavSecondPanel,
  NavMenuDesktopDropShell,
  NavMenuDesktopDropShellWrapper,
  NavMenuDesktopTopBar,
  NavMenuLgHeaderExternalLink,
  NavMenuLgHeaderLink,
  NavMenuMobileDropShell,
  NavMenuMobileHamburgerImgWrapper,
  NavMenuMobileTopBar,
  NavMenuWrapperMobile,
  StyledHeader,
  StyledNavMenuLgWrapper,
  ToggleNavParent,
  HamburgerButton,
  topBarLinksFindCouple,
  topHeaderLinkHeroTransitionClassName,
  AnnounceBannerWrapper,
  MobHeaderContent,
  MobGetStartedButton,
  StaticLogo,
} from './Header.styles';
import { ParticleJoyLogo } from './ParticleJoyLogo';
import { headerNavbarData } from '@data-ts/shared/header/header.data';
import { AnnounceBanner } from './AnnounceBanner';
import { TOP_BANNER_HEIGHT } from './AnnounceBanner.styles';
import { getRootUrl } from '@utils/urlHelpers.util';
import { CTAGatsbyLink } from '@components/CTAGatsbyLink';

interface Props {
  isTransparent?: boolean;
  scrollThreshold?: number;
  headerBackgroundColor?: JoykitColor;
  transparentJoyLogoColor?: JoykitColor;
  transparentHeaderLinkVariant?: LinkVariant;
  transparentHeaderButtonVariant?: ButtonVariant;
  page?: MarcomPageName;
}

const Header = React.forwardRef<HTMLElement, Props>(
  (
    {
      isTransparent = false,
      scrollThreshold: defaultScrollThreshold = 0,
      headerBackgroundColor = 'navbar',
      transparentJoyLogoColor = 'mono7',
      transparentHeaderLinkVariant = 'humanaLink',
      transparentHeaderButtonVariant = 'yellowPrimary',
      page,
    },
    ref
  ) => {
    const [openedMenu, setOpened] = useState<string | null>(null);
    const [isOpening, setIsOpening] = useState(false);
    const [isScrollCollapsed, setIsScrollCollapsed] = useState(false);
    const [isAnnounceCollapsed, setIsAnnounceCollapsed] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [timerId, setTimerId] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [planYourWeddingWidth, setPlanYourWeddingWidth] = useState(0);

    useEffect(() => {
      const onWindowScroll = () => {
        const currentScroll = window.pageYOffset;
        if (currentScroll > TOP_BANNER_HEIGHT && !isAnnounceCollapsed) {
          setIsAnnounceCollapsed(true);
        } else if (currentScroll <= TOP_BANNER_HEIGHT && isAnnounceCollapsed) {
          setIsAnnounceCollapsed(false);
        }

        const scrollThreshold = ((162 - 80) * 3) / 2;
        if (currentScroll > scrollThreshold && !isScrollCollapsed) {
          setIsScrollCollapsed(true);
        } else if (currentScroll <= scrollThreshold && isScrollCollapsed) {
          setIsScrollCollapsed(false);
        }

        const waterfallSection = document.getElementById('Waterfall_CTA');
        const abcSection = document.getElementById(abcSectionSectionId({ page }));
        const footer = document.getElementById(footerPageId({ page }));

        if (
          (waterfallSection && currentScroll >= waterfallSection.offsetTop) ||
          (abcSection && currentScroll >= abcSection.offsetTop) ||
          (footer && currentScroll >= footer.offsetTop)
        ) {
          if (!hidden) {
            setHidden(true);
          }
        } else {
          if (hidden) {
            setHidden(false);
          }
        }
      };
      // use passive listeners here:
      // https://web.dev/uses-passive-event-listeners/?utm_source=lighthouse&utm_medium=devtools
      window.addEventListener('scroll', onWindowScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', onWindowScroll);
      };
    }, [defaultScrollThreshold, setHidden, hidden, isScrollCollapsed, page, isAnnounceCollapsed]);

    const footerPageId = ({ page }: { page?: MarcomPageName }) => {
      switch (page) {
        case 'home':
          return 'Home_Footer';
        case 'wedding-website':
          return 'Wedding_Websites_Footer';
        case 'guest-list':
          return 'Guest_List_Footer';
        case 'online-rsvp':
          return 'RSVP_Footer';
        case 'save-the-date':
          return 'Save_the_Dates_Footer';
        case 'registry':
          return 'Registry_Footer';
        case 'app':
          return 'App_Footer';
        default:
          return 'mainLayoutFooter';
      }
    };

    const abcSectionSectionId = ({ page }: { page?: MarcomPageName }) => {
      switch (page) {
        case 'home':
          return 'Home_ABC_CTA';
        case 'wedding-website':
          return 'Wedding_Websites_ABC_CTA';
        case 'guest-list':
          return 'Guest_List_ABC_CTA';
        case 'online-rsvp':
          return 'RSVP_ABC_CTA';
        case 'save-the-date':
          return 'Save_the_Dates_ABC_CTA';
        case 'registry':
          return 'Registry_ABC_CTA';
        case 'app':
          return 'App_ABC_CTA';
        default:
          return 'planningShoesVideoContainer';
      }
    };

    const setOpenedMenu = (menu: string | null) => {
      if (menu === openedMenu) return;

      if (menu === 'mobileNavMenu' || openedMenu === 'mobileNavMenu') {
        setOpened(menu);
      } else {
        if (menu === 'resources' && document) {
          setPlanYourWeddingWidth(document.getElementById('menu2features')?.offsetWidth || 0);
        }

        if (menu === 'planYourWedding' || menu === 'resources') {
          setIsOpening(true);
          setOpened(menu);
        } else {
          setIsOpening(false);
          setTimerId(
            setTimeout(() => {
              setOpened(menu);
            }, 500)
          );
        }
      }
    };

    useEffect(() => {
      return () => {
        if (timerId) {
          clearTimeout(timerId);
        }
      };
    }, [timerId]);

    const closeOpenedMenu = (event: React.MouseEvent) => {
      event.preventDefault();
      if (openedMenu) setOpenedMenu(null);
    };

    const toggleOpenedMenu = (menu: string, canClose: boolean = true) => {
      if (openedMenu === menu && canClose) {
        setOpenedMenu(null);
      } else {
        setOpenedMenu(menu);
      }
    };

    const actualIsTransparent = openedMenu || isScrollCollapsed ? false : isTransparent;
    let buttonVariant = actualIsTransparent ? transparentHeaderButtonVariant : 'primary';

    // Set button variant for the wedding-websites page.
    if ((page === 'wedding-website' || page === 'home') && actualIsTransparent) {
      buttonVariant = 'primeWeddingWebsites';
    } else if (page === 'wedding-website' || page === 'home') {
      buttonVariant = 'primary';
    }

    const linkVariant = actualIsTransparent ? transparentHeaderLinkVariant : 'primary';
    const logoColor: JoykitColor = actualIsTransparent && !openedMenu ? transparentJoyLogoColor : 'purple6';

    const renderNavMenuLink = (id: string, title: string, to?: string, href?: string) => {
      return href ? (
        <NavMenuLgHeaderExternalLink id={id} href={href} variant="navLink" key={id}>
          <Text variant="navChild36" tagName="span">
            {title}
          </Text>
        </NavMenuLgHeaderExternalLink>
      ) : to ? (
        <NavMenuLgHeaderLink id={id} to={to} variant="navLink" key={id}>
          <Text variant="navChild36" tagName="span">
            {title}
          </Text>
        </NavMenuLgHeaderLink>
      ) : null;
    };

    const renderMobileNavMenuLink = (id: string, title: string, to?: string, href?: string) => {
      return href ? (
        <MobileNavHeaderExternalLink id={'mobile-' + id} href={href} variant="navLink" key={id}>
          <Text variant="navChild36" tagName="span">
            {title}
          </Text>
        </MobileNavHeaderExternalLink>
      ) : to ? (
        <MobileNavHeaderLink id={'mobile-' + id} to={to} variant="navLink" key={id}>
          <Text variant="navChild36" tagName="span">
            {title}
          </Text>
        </MobileNavHeaderLink>
      ) : null;
    };

    return (
      <>
        <StyledHeader
          id="page_header"
          ref={ref}
          isTransparent={actualIsTransparent}
          backgroundColor={headerBackgroundColor}
          isMenuOpen={!!openedMenu}
          onMouseLeave={event => {
            event.preventDefault();
            if (openedMenu !== 'mobileNavMenu') setOpenedMenu(null);
          }}
          hiddenMenu={hidden}
          isScrollCollapsed={isScrollCollapsed}
        >
          {page != 'registry' ? (
            <AnnounceBannerWrapper isScrollCollapsed={isScrollCollapsed}>
              <AnnounceBanner page={page} />
            </AnnounceBannerWrapper>
          ) : null}
          <>
            <NavMenuDesktopTopBar hiddenMenu={hidden} isScrollCollapsed={isScrollCollapsed}>
              <DesktopColumns verticalAlign="center">
                <Columns.Column width="half">
                  <div className="nav-menu-left">
                    <ParticleJoyLogo
                      id="desktop-joy-logo"
                      variant={linkVariant}
                      logoColor={logoColor}
                      isScrollCollapsed={isScrollCollapsed}
                    />
                    <CTALink
                      id="menu2features"
                      variant={linkVariant}
                      href="#"
                      onMouseEnter={event => {
                        event.preventDefault();
                        toggleOpenedMenu('planYourWedding', false);
                      }}
                      onClick={event => {
                        event.preventDefault();
                        toggleOpenedMenu('planYourWedding');
                      }}
                      css={{ zIndex: 1 }}
                    >
                      <ToggleNavParent
                        variant="navParent18"
                        tagName="span"
                        isActive={openedMenu === 'planYourWedding'}
                        className={topHeaderLinkHeroTransitionClassName}
                      >
                        <span style={{ whiteSpace: 'nowrap' }}>Plan your wedding</span>
                        <JoyHeartIconSvg />
                      </ToggleNavParent>
                    </CTALink>
                    <CTALink
                      id="menu3features"
                      variant={linkVariant}
                      href="#"
                      onMouseEnter={event => {
                        event.preventDefault();
                        toggleOpenedMenu('resources', false);
                      }}
                      onClick={event => {
                        event.preventDefault();
                        toggleOpenedMenu('resources');
                      }}
                      css={{ zIndex: 1 }}
                    >
                      <ToggleNavParent
                        variant="navParent18"
                        tagName="span"
                        isActive={openedMenu === 'resources'}
                        className={topHeaderLinkHeroTransitionClassName}
                      >
                        <span>Resources</span>
                        <JoyHeartIconSvg />
                      </ToggleNavParent>
                    </CTALink>
                  </div>
                </Columns.Column>
                <Columns.Column width="half">
                  <div className="nav-menu-right">
                    <CTALink id="menu4features" variant={linkVariant} href="/find-a-couple">
                      <ToggleNavParent
                        variant="navParent18"
                        tagName="span"
                        isActive={page === 'find-a-couple'}
                        className={topHeaderLinkHeroTransitionClassName + ' ' + topBarLinksFindCouple}
                      >
                        <span>Find a Couple</span>
                      </ToggleNavParent>
                    </CTALink>
                    <CTALink id="menu2login" href={getRootUrl('/login')} variant={linkVariant}>
                      <ToggleNavParent variant="navParent18" tagName="span" className={topHeaderLinkHeroTransitionClassName}>
                        <span>Sign In</span>
                      </ToggleNavParent>
                    </CTALink>
                    <StyledGetStartedButton id="menu2create" page={page} variant={buttonVariant}>
                      Get Started
                    </StyledGetStartedButton>
                  </div>
                </Columns.Column>
              </DesktopColumns>
            </NavMenuDesktopTopBar>
            <NavMenuDesktopDropShellWrapper isOpen={!!openedMenu} isOpening={isOpening} isScrollCollapsed={isScrollCollapsed}>
              <NavMenuDesktopDropShell>
                <Columns>
                  <Columns.Column width="full">
                    <Flex alignItems={'flex-start'}>
                      {openedMenu === 'planYourWedding' ? (
                        <StyledNavMenuLgWrapper flexDirection={'column'}>
                          {headerNavbarData.planYourWedding.map(navItem =>
                            renderNavMenuLink(navItem.id, navItem.title, navItem.to, navItem.href)
                          )}
                        </StyledNavMenuLgWrapper>
                      ) : openedMenu === 'resources' ? (
                        <StyledNavMenuLgWrapper flexDirection={'column'} left={planYourWeddingWidth}>
                          {headerNavbarData.resources.map(navItem =>
                            renderNavMenuLink(navItem.id, navItem.title, navItem.to, navItem.href)
                          )}
                        </StyledNavMenuLgWrapper>
                      ) : null}
                    </Flex>
                  </Columns.Column>
                </Columns>
              </NavMenuDesktopDropShell>
            </NavMenuDesktopDropShellWrapper>
          </>
          <NavMenuWrapperMobile isScrollCollapsed={isAnnounceCollapsed}>
            <NavMenuMobileTopBar isOpen={!!openedMenu}>
              <MobHeaderContent alignItems="center">
                <Box paddingLeft={5}>
                  <HamburgerButton
                    id="mobile-nav-menu"
                    onClick={() => setOpenedMenu(openedMenu ? null : 'mobileNavMenu')}
                    isClosed={openedMenu}
                  >
                    <NavMenuMobileHamburgerImgWrapper variant={openedMenu ? 'primary' : linkVariant}>
                      {!openedMenu ? <RingsClosedIcon /> : <JoykitIcon name="notAttending" color="neutra6" />}
                    </NavMenuMobileHamburgerImgWrapper>
                  </HamburgerButton>
                </Box>
                {!isScrollCollapsed && !openedMenu ? (
                  <ParticleJoyLogo id="mobile-joy-logo" variant={linkVariant} logoColor={logoColor} isScrollCollapsed={isScrollCollapsed} />
                ) : null}
                {openedMenu || !isScrollCollapsed ? (
                  <div />
                ) : (
                  <MobGetStartedButton id="menu2getStartedMobile" variant="secondary" page={page}>
                    Get Started
                  </MobGetStartedButton>
                )}
                {openedMenu === 'mobileNavMenu' ? (
                  <Flex justifyContent="flex-end">
                    <CTAGatsbyLink id={'home-page'} to={'/'}>
                      <StaticLogo src={'common/brand/png_purple5/logo_xl.png'} alt="Joy Logo" />
                    </CTAGatsbyLink>
                  </Flex>
                ) : null}
              </MobHeaderContent>
            </NavMenuMobileTopBar>

            <NavMenuMobileDropShell isOpen={!!openedMenu} isScrollCollapsed={isAnnounceCollapsed}>
              {openedMenu === 'mobileNavMenu' && (
                <>
                  <div style={{ visibility: openedMenu === 'mobileNavMenu' ? 'visible' : 'hidden', paddingLeft: 24, paddingRight: 24 }}>
                    {headerNavbarData.planYourWedding.map(navItem =>
                      renderMobileNavMenuLink(navItem.id, navItem.title, navItem.to, navItem.href)
                    )}
                  </div>
                  <MobileNavSecondPanel>
                    <MobileGetStartedButton id="menu2createMobile" page={page}>
                      Get Started
                    </MobileGetStartedButton>
                    <MobileLoginButton id="menu2meMobile" forwardedAs={'a'} href={getRootUrl('/login')} variant={'secondary'}>
                      Sign In
                    </MobileLoginButton>
                    <MobileFindButton id="menu2findMobile" href="/find-a-couple">
                      Find a Couple
                    </MobileFindButton>
                    <MobileAccordion headerText="Resources" style={{ marginTop: '85px', paddingBottom: '80px' }}>
                      {headerNavbarData.resources.map(navItem =>
                        renderMobileNavMenuLink(navItem.id, navItem.title, navItem.to, navItem.href)
                      )}
                    </MobileAccordion>
                  </MobileNavSecondPanel>
                </>
              )}
            </NavMenuMobileDropShell>
          </NavMenuWrapperMobile>
        </StyledHeader>

        {/* stub element for correct spacing depending on isTransparent*/}
        {!isTransparent && <HeaderSpacingStub />}
      </>
    );
  }
);

Header.displayName = 'Header';

export { Header };
